import { useCallback, useEffect } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { DispositiveProvisionsModalBody } from '@/modules/dispositiveProvisions/DispositiveProvisionsSplitScreenModal/DispositiveProvisionsModalBody';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';

/** Check if a section is a dispositive provisions section for entities */
function isEntityDPSection(section: EditEntitySection): boolean {
  return (
    section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1 ||
    section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2
  );
}
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { useEditTestamentaryEntitySplitScreenModalContext } from './contexts/editTestamentaryEntitySplitScreenModal.context';
import {
  EditTestamentaryEntitySection,
  isEditTestamentaryEntitySection,
} from './EditTestamentaryEntitySplitScreen.types';
import { EditTestamentaryEntitySplitScreenForm } from './EditTestamentaryEntitySplitScreenForm';

export function EditTestamentaryEntitySplitScreenRightContent() {
  const { showFeedback } = useFeedback();

  const {
    currentSection,
    headerRef,
    footerRef,
    initialFormValues,
    hasError,
    handleCloseModal,
    isDPSection,
    setCurrentSection,
    selectedFirstDeathClientId,
    setSelectedFirstDeathClientId,
  } = useEditTestamentaryEntitySplitScreenModalContext();

  const { primaryClients } = useHouseholdDetailsContext();
  const { resetDiagramSyncState } = useDispositiveDiagramSyncContext();

  useEffect(() => {
    if (!hasError) return;
    showFeedback(
      'Could not load form data. Please refresh the page and try again.'
    );
  }, [hasError, showFeedback]);

  const handleAfterSubmit = useCallback(
    (afterSubmitSection?: EditTestamentaryEntitySection) => {
      if (afterSubmitSection) {
        // DispositiveDiagramSyncProvider persists above the keyed
        // DispositiveProvisionsModalBody, so reset its state before switching
        // sections or the next diagram reads the previous section's overlays.
        resetDiagramSyncState();
        setCurrentSection(afterSubmitSection);
      } else {
        handleCloseModal();
      }
    },
    [handleCloseModal, setCurrentSection, resetDiagramSyncState]
  );

  const handleDPAfterSubmit = useCallback(
    (afterSubmitSection?: EditEntitySection) => {
      // Handle death order changes: when the user selects a different death order
      // via the DeathOrderSelector, it passes DISPOSITIVE_PROVISIONS_GRANTOR_1 or
      // DISPOSITIVE_PROVISIONS_GRANTOR_2. For testamentary entities, we only have
      // one DISPOSITIONS section, so we just update the selected death client ID
      // instead of switching sections.
      if (afterSubmitSection && isEntityDPSection(afterSubmitSection)) {
        const newFirstDeathClientId =
          afterSubmitSection ===
          EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1
            ? (primaryClients?.[0]?.id ?? null)
            : (primaryClients?.[1]?.id ?? null);
        // Reset before switching death orders so the next diagram doesn't
        // read overlays resolved for the previous first-death client.
        resetDiagramSyncState();
        setSelectedFirstDeathClientId(newFirstDeathClientId);
        return;
      }

      if (
        afterSubmitSection &&
        isEditTestamentaryEntitySection(afterSubmitSection)
      ) {
        handleAfterSubmit(afterSubmitSection);
      } else {
        handleAfterSubmit();
      }
    },
    [
      handleAfterSubmit,
      primaryClients,
      resetDiagramSyncState,
      setSelectedFirstDeathClientId,
    ]
  );

  if (isDPSection) {
    // Use selectedFirstDeathClientId if available, otherwise fall back to first primary client.
    // selectedFirstDeathClientId is updated when the user changes the death order via
    // the DeathOrderSelector in the dispositions form header.
    const firstDeathClientId =
      selectedFirstDeathClientId || primaryClients?.[0]?.id || '';

    return (
      <DispositiveProvisionsModalBody
        key={`${currentSection}-${firstDeathClientId}`}
        headerRef={headerRef}
        footerRef={footerRef}
        onClose={handleCloseModal}
        onSwitchSectionAfterSubmit={handleDPAfterSubmit}
        variant="editTestamentaryEntity"
        firstDeathClientId={firstDeathClientId}
        onTestamentaryEntitySectionSubmit={handleAfterSubmit}
      />
    );
  }

  if (!initialFormValues) {
    return (
      <Loader
        boxProps={{
          sx: {
            textAlign: 'center',
            my: 3,
          },
        }}
      />
    );
  }

  return (
    <EditTestamentaryEntitySplitScreenForm
      initialFormValues={initialFormValues}
    />
  );
}
